import { BrainCircuit, ExternalLink, RotateCcw } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { CompanyBrainError, useAuth } from '../auth/AuthProvider';

type OpenState =
  | { status: 'idle' }
  | { status: 'opening' }
  | { status: 'failed'; message: string; reason: string };

/**
 * Company Brain entry point.
 *
 * The button never stays stuck on «Открываем…»: the request has a timeout, a
 * failure shows a readable message plus «Повторить», and the technical reason
 * is logged for support.
 */
export function CompanyBrainButton(input: { variant: 'rail' | 'header' }): ReactNode {
  const auth = useAuth();
  const [state, setState] = useState<OpenState>({ status: 'idle' });
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  async function open(): Promise<void> {
    if (state.status === 'opening') return;
    setState({ status: 'opening' });
    try {
      await auth.openCompanyBrain();
      // Navigation is under way; if the page is still here, release the button.
      if (mounted.current) setState({ status: 'idle' });
    } catch (error) {
      if (!mounted.current) return;
      setState({
        status: 'failed',
        message: error instanceof Error ? error.message : 'Не удалось открыть Company Brain',
        reason: error instanceof CompanyBrainError ? error.reason : 'unknown',
      });
    }
  }

  const caption = state.status === 'opening'
    ? 'Открываем…'
    : state.status === 'failed'
      ? state.message
      : 'Интервью и знания';

  return (
    <div className={`brain-launcher brain-launcher-${input.variant}`}>
      <button
        type="button"
        className={input.variant === 'rail' ? 'global-nav-link global-nav-link-external' : 'brain-header-link'}
        aria-label={input.variant === 'rail'
          ? 'Открыть Company Brain — компании, интервью и знания'
          : 'Открыть Company Brain из воронки'}
        aria-busy={state.status === 'opening'}
        title={state.status === 'failed' ? `${state.message} (${state.reason})` : `Открыть Company Brain (${auth.companyBrainUrl})`}
        onClick={() => void open()}
        disabled={state.status === 'opening'}
      >
        <BrainCircuit size={input.variant === 'rail' ? 20 : 18} aria-hidden />
        <span className={input.variant === 'rail' ? 'global-nav-label brain-nav-copy' : undefined}>
          <strong>Company Brain</strong>
          <small className={state.status === 'failed' ? 'brain-status-error' : undefined}>{caption}</small>
        </span>
        {state.status === 'failed'
          ? null
          : <ExternalLink className="global-nav-external-icon" size={13} aria-hidden />}
      </button>
      {state.status === 'failed' ? (
        <button className="brain-retry" type="button" onClick={() => void open()}>
          <RotateCcw size={13} aria-hidden />Повторить
        </button>
      ) : null}
      {state.status === 'failed' ? (
        <span role="alert" className="sr-only">{`Company Brain не открылся: ${state.message}`}</span>
      ) : null}
    </div>
  );
}
